/**
 * What a field *is*, and what that implies for its column.
 *
 * Roles describe the data, not the grid. Two roles settling on the same behaviour is fine —
 * `identifier` and `text` both hide from the charts panel for different reasons, and either
 * could change without disturbing the other.
 */

import type { GridValueFormatter } from '@mui/x-data-grid';

import { formatCurrency } from '../../../utils/GridHelper';
import { formatDateForDisplay } from '../../../utils/DateHelper.tsx';
import type { SummaryReportFieldConfiguration } from '../utils/summaryReportColumns.ts';

export type SummaryReportFieldRole =
    /** A value worth grouping by: type, status, agency, county. The default shape. */
    | 'category'
    /** Unique or near-unique per record — no chart can make categories of it. */
    | 'identifier'
    /** Free text. Same treatment as an identifier, for a different reason. */
    | 'text'
    /** A raw timestamp — distinct per record, displayed the way every grid displays one. */
    | 'date'
    /** Money. Totals, and reads as money wherever it is totalled. */
    | 'currency'
    /** A measure that totals meaningfully — acres, counts of things. */
    | 'quantity'
    /** A fiscal year. A number, but written plainly: 2025, never 2,025. */
    | 'year'
    /** A rate, duration or score. Averages, because totalling it means nothing. */
    | 'rate'
    /** A rate that reads with its sign attached. */
    | 'percentage';

/** Plain number display — thousands separated, at most two decimals. */
const formatQuantity: GridValueFormatter = (value) =>
    value != null ? (value as number).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '';

/** What each role settles, in the column configuration's terms. */
const ROLE_SETTINGS: Record<SummaryReportFieldRole, Partial<SummaryReportFieldConfiguration>> = {
    category: {},
    identifier: { chartable: false },
    text: { chartable: false },
    // Explicit, because the date column type inherits `chartable: true` from the string one.
    date: {
        chartable: false,
        valueFormatter: (value) => (value ? formatDateForDisplay(value as Date) : ''),
    },
    currency: { valueFormatter: (value) => formatCurrency(value as number | null) },
    quantity: { valueFormatter: formatQuantity },
    year: { valueFormatter: (value) => (value != null ? (value as number).toString() : '') },
    // One choice, not a preference: the charts panel takes the first aggregation a field
    // allows, so naming a single one is what keeps `sum` off a number that can't be summed.
    rate: { availableAggregations: ['avg'] },
    percentage: {
        availableAggregations: ['avg'],
        valueFormatter: (value) => (value != null ? `${value}%` : ''),
    },
};

/**
 * Roles totalled in the footer and in every group row.
 *
 * `rate` and `percentage` are absent on purpose: they aggregate only when something asks
 * them to, and they average when it happens.
 */
const AGGREGATING_ROLES: SummaryReportFieldRole[] = ['currency', 'quantity'];

/** Roles whose aggregate reads as money rather than as a plain number. */
const COST_ROLES: SummaryReportFieldRole[] = ['currency'];

/** One field, as the configuration declares it. */
export interface SummaryReportFieldDeclaration extends Omit<
    SummaryReportFieldConfiguration,
    'field'
> {
    field: string;
    role: SummaryReportFieldRole;
}

/**
 * A declaration, resolved into the column configuration the grid builder understands.
 *
 * Anything stated on the field wins over its role — the escape hatch for a one-off that
 * shares a role with fifty others but needs one thing of its own.
 */
export function resolveFieldRole(
    declaration: SummaryReportFieldDeclaration
): SummaryReportFieldConfiguration {
    const { role, ...overrides } = declaration;
    return { ...ROLE_SETTINGS[role], ...overrides };
}

/** The aggregation a role's fields start with, if any. */
export function defaultAggregationFor(role: SummaryReportFieldRole): 'sum' | undefined {
    return AGGREGATING_ROLES.includes(role) ? 'sum' : undefined;
}

/** Whether a role's aggregate should be formatted as money. */
export function isCostRole(role: SummaryReportFieldRole): boolean {
    return COST_ROLES.includes(role);
}
